/*
  <load-more>: the Show more button of a paginated list (snippets/pagination.liquid). The button is a link to the
  next page; this element asks that page's section from the Section Rendering API instead, appends its items to the
  list and updates the count line, the bar and the link from the answer. Without it the link simply navigates.

  - One request at a time. A failed request keeps the list, shows a message and leaves the link for another try.
  - After a click focus moves to the first new item and the count line, a status region, announces the new count.
    Loads that start by themselves never move focus.
  - With data-auto the next page loads while the button is within one and a half screens of the window, and again after
    each insert for as long as it stays there. The button stays, for keyboards.
  - The address does not change: a reload shows the first page again, the same on every list.
  - Filters replace the markup around the list, which disconnects this element and stops its request.
*/
if (!customElements.get('load-more')) {
  // Nothing is kept: a page is asked for when the shopper or the scroll wants it.
  const answers = sectionAnswers({ size: 0 });

  /** True for a click that would open its link in this page: the main button, no modifier key, not handled already. */
  function opensHere(event) {
    return !(
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    );
  }

  class LoadMore extends HTMLElement {
    connectedCallback() {
      this.list = document.getElementById(this.dataset.list);
      this.link = this.querySelector('.load-more__button');
      this.loading = false;
      this.failed = false;
      this.lifetime = new AbortController();
      if (!this.list) return;

      const { signal } = this.lifetime;
      this.addEventListener('click', (event) => this.onClick(event), { signal });
      // Following a result while a page loads goes first. A click that opens the link elsewhere (a new tab, a download,
      // or one another script already handled) leaves this page where it is, and so leaves its request running.
      this.list.addEventListener(
        'click',
        (event) => {
          if (event.target.closest('a[href]') && opensHere(event)) this.request?.abort();
        },
        { signal }
      );
      // A page restored from the back/forward cache comes back as it was left; a request cut off meanwhile is not
      // running.
      window.addEventListener('pageshow', (event) => event.persisted && this.restart(), { signal });
      this.watch();
    }

    disconnectedCallback() {
      this.lifetime?.abort();
      this.request?.abort();
      this.observer?.disconnect();
    }

    onClick(event) {
      if (!event.target.closest('.load-more__button')) return;
      // Let the browser open the link when the click means something else (new tab, download).
      if (!opensHere(event)) return;
      event.preventDefault();
      this.load(true);
    }

    /** Infinite scroll: the observer reports once for every time it starts watching, so each insert watches again. */
    watch() {
      if (!this.hasAttribute('data-auto') || !this.link) return;
      this.observer ??= new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting) && !this.failed) this.load(false);
        },
        { rootMargin: '0px 0px 150% 0px' }
      );
      this.observer.unobserve(this);
      this.observer.observe(this);
    }

    restart() {
      this.request?.abort();
      this.setBusy(false);
      this.failed = false;
      this.watch();
    }

    async load(byUser) {
      if (this.loading || !this.link) return;
      this.setBusy(true);
      this.failed = false;
      this.showError(false);
      this.request = new AbortController();

      let next;
      let left = false;
      try {
        next = await this.fetchNext(this.request.signal);
      } catch (error) {
        // An abort is a click on a result: the page is on its way out, and the control is left as it was.
        if (error.name === 'AbortError') {
          left = true;
        } else {
          console.warn('Core: the next page could not be loaded', error);
          this.failed = true;
          this.showError(true);
        }
      } finally {
        this.setBusy(false);
      }
      // A filter may have replaced this control while the answer was on its way: nothing is left to add to.
      if (left || !this.isConnected) return;
      // Everything that could fail has happened by now: the answer is applied in one step, so a retry never repeats it.
      if (next) this.apply(next, byUser);
      if (!this.failed && this.link) this.watch();
    }

    /** The next page's items and control, read from the section's answer; throws before anything is changed. */
    async fetchNext(signal) {
      const url = new URL(this.link.href, window.location.href);
      url.searchParams.set('section_id', this.dataset.section);
      const page = await answers.page(url.href, signal);
      const items = [...(page.getElementById(this.dataset.list)?.children ?? [])];
      const control = page.querySelector(`load-more[data-list="${CSS.escape(this.dataset.list)}"]`);
      const count = control?.querySelector('.load-more__count');
      const fill = control?.querySelector('.load-more__fill');
      if (!count || !fill) throw new Error('The answer has no list');
      return {
        items,
        count: count.textContent,
        share: fill.style.getPropertyValue('--load-more-share'),
        href: control.querySelector('.load-more__button')?.getAttribute('href') ?? null,
      };
    }

    /**
     * Items go before a tile pinned to the end of the list, so that tile stays last; then the control takes the
     * answer's.
     */
    apply({ items, count, share, href }, byUser) {
      const pinned = this.list.querySelector(':scope > [data-pinned-end]');
      for (const item of items) {
        this.list.insertBefore(document.adoptNode(item), pinned);
      }
      this.querySelector('.load-more__count').textContent = count;
      this.querySelector('.load-more__fill').style.setProperty('--load-more-share', share);
      // The answer's link is the page after this one, or none on the last page.
      if (href) {
        this.link.setAttribute('href', href);
      } else {
        this.link.remove();
        this.link = null;
        this.observer?.disconnect();
      }
      if (byUser) items[0]?.querySelector('a[href]')?.focus();
    }

    setBusy(busy) {
      this.loading = busy;
      if (!this.link) return;
      for (const name of ['aria-disabled', 'aria-busy']) {
        if (busy) this.link.setAttribute(name, 'true');
        else this.link.removeAttribute(name);
      }
    }

    showError(visible) {
      const notice = this.querySelector('.load-more__error');
      if (!notice) return;
      notice.querySelector('.notice__message').textContent = visible ? this.dataset.failed : '';
      notice.hidden = !visible;
    }
  }

  customElements.define('load-more', LoadMore);
}
